import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Pagination, Empty } from 'antd';
import { WalletOutlined } from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import lib from '../../../assets/utils/lib';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { getClaimsArchiveScope, withClaimsArchiveScope } from '../claimsArchiveHelpers';
import {
  getClaimsApprovedTotal,
  getPaApprovedTotal,
  getSubmittedTotal,
} from '../claimFinancialHelpers';
import { useSearchParams } from 'react-router-dom';
import './PaymentBatches.css';
import './PaymentDrilldown.css';

const PAID_ACCENT = '#49B870';

function getMonthKey(month) {
  if (!month) return null;
  if (typeof month === 'string') return month;
  return month.month || month.key || null;
}

function formatMonthLabel(month) {
  const key = getMonthKey(month);
  if (!key) return 'Month';
  return dayjs(`${key}-01`).format('MMMM YYYY');
}

function DrillRowSkeleton() {
  return (
    <div className="drill-row-skeleton" aria-hidden>
      <div className="drill-row-skeleton__block" style={{ width: 34, height: 34, borderRadius: 9 }} />
      <div className="drill-row-skeleton__block" style={{ width: '38%' }} />
      <div className="drill-row-skeleton__block" style={{ width: 60, marginLeft: 'auto' }} />
      <div className="drill-row-skeleton__block" style={{ width: 90 }} />
    </div>
  );
}

function formatBatchDate(value) {
  if (!value) return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return d.format('MMM D, YYYY');
}

/**
 * PaymentBatches — payment batches for a paid facility and encounter month.
 */
const PaymentBatches = ({ month, facilityId, facilityName, onBatchSelect }) => {
  const [searchParams] = useSearchParams();
  const [batches, setBatches] = useState([]);
  const [aggregates, setAggregates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const fetchingRef = useRef(false);

  const monthLabel = formatMonthLabel(month);
  const displayFacilityName = facilityName || 'Facility';
  const archiveScope = getClaimsArchiveScope(searchParams);

  const fetchBatches = useCallback(async (page = 1) => {
    if (!month || !facilityId) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    const monthKey = getMonthKey(month);
    if (!monthKey) {
      notification.error({ message: 'Error', description: 'Invalid month format' });
      fetchingRef.current = false;
      return;
    }

    setLoading(true);
    try {
      const response = await lib.getPaymentBatches(monthKey, facilityId, withClaimsArchiveScope({
        page,
        pageSize: pagination.pageSize,
      }, archiveScope));

      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch payment batches',
        });
        return;
      }

      const responseData = response?.data || {};
      const batchesList = Array.isArray(responseData.data) ? responseData.data : [];

      setBatches(batchesList);
      setAggregates(responseData.aggregates || null);
      setPagination((prev) => ({
        ...prev,
        current: responseData.pagination?.page || 1,
        pageSize: responseData.pagination?.pageSize || prev.pageSize,
        total: responseData.pagination?.total || batchesList.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching payment batches:', error);
      notification.error({ message: 'Error', description: 'Failed to fetch payment batches' });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [month, facilityId, pagination.pageSize, archiveScope]);

  useEffect(() => {
    if (month && facilityId) {
      setPagination((prev) => ({ ...prev, current: 1 }));
      fetchBatches(1);
    }
  }, [month, facilityId, fetchBatches]);

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    fetchBatches(page);
  };

  if (!month || !facilityId) {
    return <Empty description="Missing required parameters" />;
  }

  const approvedTotal = getClaimsApprovedTotal(aggregates);
  const submittedTotal = getSubmittedTotal(aggregates);
  const filedTotal = getPaApprovedTotal(aggregates);
  const batchCountLabel = loading
    ? 'Loading…'
    : `${pagination.total.toLocaleString()} batch${pagination.total === 1 ? '' : 'es'}`;

  return (
    <div className="payment-batches">
      <div className="drill-panel">
        <div className="drill-panel__head">
          <div>
            <h2>{displayFacilityName}</h2>
            <p>
              Paid · {monthLabel} · {batchCountLabel}
              {aggregates ? (
                <>
                  {' · '}{(aggregates.total_claims || 0).toLocaleString()} claims
                  {' · Submitted '}<CurrencyFormat value={submittedTotal} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                  {' · PA approved '}<CurrencyFormat value={filedTotal} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                  {' · Claims approved '}<CurrencyFormat value={approvedTotal} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                </>
              ) : null}
            </p>
          </div>
        </div>

        <div className="drill-scroll">
        {!loading ? (
          <div className="drill-cols">
            <span className="c-main">Batch</span>
            <span className="c-num">Claims</span>
            <span className="c-num">Manual</span>
            <span className="c-num">Direct</span>
            <span className="c-money">Submitted</span>
            <span className="c-money">PA approved</span>
            <span className="c-money">Claims approved</span>
            <span className="c-chev" />
          </div>
        ) : null}

        {loading ? (
          <div aria-label="Loading batches">
            {Array.from({ length: 4 }).map((_, i) => (
              <DrillRowSkeleton key={`skel-${i}`} />
            ))}
          </div>
        ) : batches.length === 0 ? (
          <div className="claims-empty">
            <WalletOutlined style={{ fontSize: 28, color: '#94a3b8' }} />
            <p className="claims-empty__title">No payment batches</p>
            <p className="claims-empty__desc">
              No paid batches found for {displayFacilityName} in {monthLabel}.
            </p>
          </div>
        ) : (
          batches.map((batch) => {
            const paidDate = formatBatchDate(batch.date_paid);
            return (
              <button
                type="button"
                key={batch.payment_batch}
                className="drill-row"
                onClick={() => onBatchSelect?.(batch)}
              >
                <div className="drill-row__main">
                  <span className="drill-row__icon" style={{ background: `${PAID_ACCENT}1a`, color: PAID_ACCENT }}>
                    <WalletOutlined />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="drill-row__title">{batch.payment_batch || 'Batch'}</div>
                    <div className="drill-row__sub">
                      {[
                        paidDate ? `Paid ${paidDate}` : null,
                        batch.payment_evidence_count
                          ? `${batch.payment_evidence_count} payment doc${batch.payment_evidence_count === 1 ? '' : 's'}`
                          : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
                <span className="drill-row__num">{(batch.claims_count || 0).toLocaleString()}</span>
                <span className="drill-row__num">{Number(batch.manual_items_count || 0).toLocaleString()}</span>
                <span className="drill-row__num">{Number(batch.direct_items_count || 0).toLocaleString()}</span>
                <span className="drill-row__money">
                  <CurrencyFormat value={getSubmittedTotal(batch)} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                </span>
                <span className="drill-row__money">
                  <CurrencyFormat value={getPaApprovedTotal(batch)} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                </span>
                <span className="drill-row__money is-accent">
                  <CurrencyFormat value={getClaimsApprovedTotal(batch)} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                </span>
                <span className="drill-row__chev">›</span>
              </button>
            );
          })
        )}
        </div>

        {!loading && pagination.total > pagination.pageSize ? (
          <div className="payment-batches-pagination">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePageChange}
              showSizeChanger={false}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PaymentBatches;
